import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc, collection, addDoc, serverTimestamp, getDocs } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase";
import Loading from "../components/Loading";
import Navbar from "../components/Navbar";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./CheckoutPage.css";

const db = getFirestore(app);
const auth = getAuth(app);

// Basic country list (can be extended)
const countryOptions = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "ES", label: "Spain" },
  { value: "IT", label: "Italy" },
  { value: "HU", label: "Hungary" },
  // Add more countries as needed
];

const CheckoutPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    address: "",
    country: "",
    phone: "",
    zipcode: "",
  });
  const [status, setStatus] = useState("");

  // Check if user is logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) navigate("/sign-in");
      else setUser(currentUser);
    });
    return () => unsubscribe();
  }, [navigate]);

  // Fetch product
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const productDoc = await getDoc(doc(db, "products", id));
        if (!productDoc.exists()) {
          setProduct(null);
          return;
        }
        const data = productDoc.data();

        // Fetch active price
        const pricesSnapshot = await getDocs(collection(db, "products", id, "prices"));
        let price = null;
        pricesSnapshot.forEach((priceDoc) => {
          const priceData = priceDoc.data();
          if (priceData.active && !price) {
            price = { id: priceDoc.id, ...priceData };
          }
        });

        setProduct({
          id: productDoc.id,
          name: data.name,
          images: data.images,
          description: data.description,
          price,
        });
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) return <Loading />;
  if (!product) return <p>Product not found.</p>;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCountryChange = (option) => {
    setFormData({ ...formData, country: option.value });
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setStatus("loading");
  try {
    await addDoc(collection(db, "inquiries"), {
      productId: product.id,
      productName: product.name,
      userId: user.uid,
      ...formData,
      submittedAt: serverTimestamp(),
    });
    setStatus("success");

    setTimeout(() => navigate("/"), 3000);
  } catch (error) {
    console.error(error);
    setStatus("error");
  }
};


  return (
    <div className="CheckoutPage_Component">
      <Navbar />
      <div className="CheckoutPage_Container">
        {/* Left: Product Summary */}
        <div className="CheckoutPage_ProductSummary">
          <img src={product.images?.[0] || "/images/default.png"} alt={product.name} />
          <div className="Product_Description">
            <h2>{product.name}</h2>
            <p className="CheckoutPage_Price">
              {product.price ? `$${(product.price.unit_amount / 100).toFixed(2)}` : "$0"}
            </p>
          </div>
        </div>

        {/* Right: Checkout Form */}
        <div className="CheckoutPage_FormContainer">
          <h2>Customer Information</h2>
          <form className="CheckoutPage_Form" onSubmit={handleSubmit}>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <h2 className="CheckoutPage_H2">Shipping Address</h2>
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              value={formData.fullName}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="address"
              placeholder="Address"
              value={formData.address}
              onChange={handleChange}
              required
            />
            <input
            type="text"
            name="zipcode"
            placeholder="ZIP Code"
            value={formData.zipcode} // <-- use zipcode
            onChange={handleChange}
            required
            />


            <Select
              options={countryOptions}
              value={countryOptions.find(c => c.value === formData.country)}
              onChange={handleCountryChange}
              placeholder="Select Country"
              className="CountryDropdown"
            />

            <PhoneInput
              country={formData.country.toLowerCase() || "us"}
              value={formData.phone}
              onChange={handlePhoneChange}
              inputStyle={{ width: "100%" }}
              placeholder="Phone (optional)"
            />

            <button type="submit" disabled={status === "loading" || status === "success"}>
              {status === "loading"
                ? "Placing order..."
                : status === "success"
                ? "Order placed ✅"
                : "Place Order"}
            </button>
            {status === "error" && <p className="error">Something went wrong. Please try again.</p>}
          </form>
        </div>
      </div>

      {status === "success" && (
        <div className="CheckoutPage_SuccessOverlay">
          <div className="CheckoutPage_SuccessMessage">
            <h2>Order successfully placed!</h2>
            <p>You will be redirected to the home page shortly...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
